import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import { BankFlowScreen } from './BankTransactionsPage';
import { useMyAccess } from '../services/conversionsApi';
import { withClientId } from '../lib/clientContext';
import { usePageStyles } from '../components/layout/pageStyles';

/**
 * The §3c debit/credit register (CONVERSIONS_SPEC.md §9).
 *
 * <p>Shares `BankFlowScreen` with the bank-statement screen because the two differ in
 * exactly one thing: which Xero shape a row produces. What makes them SEPARATE SCREENS is
 * §9's "one flow, one output" rule — offering both downloads on one row, as `ExportPage`
 * did, pushed a choice between two Xero formats onto a bookkeeper mid-task with nothing on
 * screen to inform it.</p>
 *
 * <p>⚠ Worth knowing when reading the output: unlike bank-statement import, Xero has no
 * single canonical "account transactions" import format. This shape is a register designed
 * for staff to eyeball-match against an existing bank rec — see `XeroCsvBuilder`'s
 * `AccountTransactionsHeader` remarks. The screen copy says so rather than implying an
 * import target.</p>
 */
export default function AccountTransactionsPage() {
  const { t } = useTranslation();
  const frame = usePageStyles();
  const access = useMyAccess();
  const [params] = useSearchParams();
  // In the nav's DEV section, for core's Admins only (Christian's walk of deploy #20). An old link from anyone else
  // says where the screen went, rather than opening a screen the nav no longer offers them.
  if (access.isLoading) return <div className={frame.page}><Spinner label={t('common.loading')} /></div>;
  if (!access.data?.isAdmin) {
    return (
      <div className={frame.page}>
        <MessageBar intent="info">
          <MessageBarBody>
            {t('accountTransactionsPage.devOnly')} <Link to={withClientId('/bank-transactions', params.get('clientId') ?? '')}>{t('nav.bankTransactions')}</Link>
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }
  return (
    <BankFlowScreen
      mode="accountTransactions"
      titleKey="accountTransactionsPage.title"
      subtitleKey="accountTransactionsPage.subtitle"
      noteKey="accountTransactionsPage.note"
      instructionsScreen="accountTransactions"
    />
  );
}
